import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import useNotifications from '../../../../../_lib/notifications/useNotifications';

const MAX_VISIBLE_ID_CHARS = 10;

// Show only the first 10 characters of a long opaque id, followed by an
// ellipsis. Short ids (<= 10 chars) render in full. The full id is always
// available via the copy control + native `title` tooltip.
export const truncateId = (value: string): string =>
  value.length > MAX_VISIBLE_ID_CHARS
    ? `${value.slice(0, MAX_VISIBLE_ID_CHARS)}…`
    : value;

interface TruncatedIdCellProps {
  readonly idValue: string;
}

const fallbackCopyToClipboard = (text: string, notifications: any) => {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  textArea.style.top = '-999999px';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand('copy');
    if (successful) {
      notifications.success('ID copied to clipboard');
    } else {
      notifications.error('Failed to copy ID');
    }
  } catch {
    notifications.error('Failed to copy ID');
  }

  document.body.removeChild(textArea);
};

/**
 * Cell content for opaque id columns: a truncated, single-line preview
 * (`first10…`) plus a copy-to-clipboard control that copies the FULL id. Shared
 * by every table via `IdTableCell` (BaseTable's `SmartTableCell` auto-detects id
 * columns by name), so id columns get the same compact, copyable treatment
 * everywhere.
 */
export const TruncatedIdCell: React.FC<TruncatedIdCellProps> = ({ idValue }) => {
  const notifications = useNotifications();

  const fullId = String(idValue);
  const hasCopyableId = !!idValue && idValue !== '-';

  const handleCopyToClipboard = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(fullId)
        .then(() => {
          notifications.success('ID copied to clipboard');
        })
        .catch(() => {
          fallbackCopyToClipboard(fullId, notifications);
        });
    } else {
      fallbackCopyToClipboard(fullId, notifications);
    }
  };

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        width: '100%',
        minHeight: '1.5rem',
        overflow: 'hidden',
      }}
    >
      <span style={{ whiteSpace: 'nowrap' }} title={fullId}>
        {truncateId(fullId)}
      </span>
      {hasCopyableId ? (
        <Tooltip title="Copy ID to clipboard">
          <IconButton
            size="small"
            aria-label="Copy ID to clipboard"
            onClick={handleCopyToClipboard}
            sx={{
              padding: '0.25rem',
              minWidth: 'auto',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ContentCopyIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </div>
  );
};
